import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDateShort } from "@/lib/api";
import { StatusPill } from "@/lib/orderStatus";
import { StoreFilter, getActiveStore } from "@/components/StoreFilter";
import RecentActivity from "@/components/RecentActivity";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Paintbrush,
  CheckCircle2,
  DollarSign,
  ArrowUpRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KpiCard = ({ icon: Icon, label, value, sub, tone = "default", testid }) => {
  const toneClass = {
    default: "text-foreground",
    success: "text-primary",
    paint: "text-[#D66853]",
    warn: "text-[#E5A93D]",
  }[tone];
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className={`mt-3 font-heading text-3xl font-bold tracking-tight num ${toneClass}`}>{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-md bg-secondary grid place-items-center text-primary">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
};

const StripeKpiCard = ({ data, payouts }) => {
  const salesNet = (data.sales_net || 0) / 100;
  const cost = (data.total_stripe_cost || 0) / 100;
  // Merchant "net" = money earned after Stripe's cut. The backend's `net`
  // field is change-in-Stripe-balance, which incorrectly subtracts payouts
  // to your bank (same money, just relocated).
  const net = salesNet - cost;
  const refunds = (data.sales_refunds || 0) / 100;
  const upcoming = payouts?.upcoming || [];
  const batches = payouts?.pending_batches || [];
  const nextPayout = upcoming.length > 0 ? upcoming[0] : null;
  const nextBatch = batches.length > 0 ? batches[0] : null;
  const pending = payouts?.balance?.pending?.reduce((s, b) => s + b.amount, 0) || 0;
  return (
    <div className="stat-card" data-testid="kpi-stripe-net">
      <div className="flex items-start justify-between mb-2">
        <div className="label-eyebrow">Stripe (this month)</div>
        <div className="w-10 h-10 rounded-md bg-secondary grid place-items-center text-primary">
          <DollarSign className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Sales net</span>
          <span className={`num font-semibold ${salesNet >= 0 ? "text-primary" : "text-[#a44231]"}`}>
            {salesNet >= 0 ? "+" : ""}{fmtCurrency(salesNet)}
          </span>
        </div>
        {refunds > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground pl-3">↳ refunds</span>
            <span className="num text-[#a44231]">-{fmtCurrency(refunds)}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Stripe fees</span>
          <span className="num font-semibold text-[#a44231]">
            {cost > 0 ? `-${fmtCurrency(cost)}` : fmtCurrency(0)}
          </span>
        </div>
        <div className="border-t border-border/60 pt-1.5 flex items-center justify-between">
          <span className="text-muted-foreground text-xs uppercase tracking-wider">Net</span>
          <span className={`num font-bold text-lg ${net >= 0 ? "text-foreground" : "text-[#a44231]"}`}>
            {fmtCurrency(net)}
          </span>
        </div>
      </div>
      {(nextPayout || nextBatch || pending > 0) && (
        <Link
          to="/reports?tab=payouts"
          data-testid="dashboard-payout-link"
          className="mt-3 pt-3 border-t border-border/60 block hover:bg-secondary/40 -mx-6 px-6 py-2 transition-colors"
        >
          {nextPayout ? (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Next payout</span>
              <span className="num font-semibold">
                {fmtCurrency((nextPayout.amount || 0) / 100)}
                <span className="text-muted-foreground ml-2 font-normal">
                  {nextPayout.arrival_date
                    ? new Date(nextPayout.arrival_date * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : ""}
                </span>
              </span>
            </div>
          ) : nextBatch ? (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                Arrives {new Date(nextBatch.available_on + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {batches.length > 1 && ` (+${batches.length - 1} more)`}
              </span>
              <span className="num font-semibold">
                {fmtCurrency((nextBatch.net || 0) / 100)}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Settling to bank</span>
              <span className="num text-muted-foreground">{fmtCurrency(pending / 100)}</span>
            </div>
          )}
        </Link>
      )}
    </div>
  );
};


export default function Dashboard() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [stripeKpi, setStripeKpi] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [activity, setActivity] = useState([]);
  const [store, setStore] = useState(getActiveStore());

  const load = async (currentStore = store) => {
    const params = currentStore !== "all" ? { store: currentStore } : {};
    const { data } = await api.get("/dashboard", { params });
    setData(data);
    try {
      const { data: nd } = await api.get("/stripe/net-deposits");
      if (nd?.ok) setStripeKpi(nd);
    } catch {
      setStripeKpi(null);
    }
    try {
      const { data: po } = await api.get("/stripe/payouts", { params: { limit: 4 } });
      if (po?.ok) setPayouts(po);
    } catch {
      setPayouts(null);
    }
    try {
      const { data: a } = await api.get("/dashboard/activity", { params: { limit: 20 } });
      setActivity(a?.events || []);
    } catch {
      setActivity([]);
    }
  };

  useEffect(() => { load(store); }, [store]);

  // Refresh activity every 60s so newly-synced orders / payments show up.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data: a } = await api.get("/dashboard/activity", { params: { limit: 20 } });
        setActivity(a?.events || []);
      } catch (e) {
        console.debug("activity refresh failed", e);
      }
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const syncBase44 = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/orders/sync/base44");
      if (data.ok === false) {
        toast.error(data.error || "Sync failed");
      } else {
        toast.success(`Synced ${data.synced} order${data.synced === 1 ? "" : "s"} (${data.inserted} new)`);
      }
      await load(store);
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!data) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  const k = data.kpis;

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Today</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Operations overview
          </h1>
          <p className="text-muted-foreground mt-1">
            {store === "all"
              ? "North-star metrics across orders, paint, and delivery."
              : `Filtered to ${store} store.`}
          </p>
        </div>
        <div className="flex gap-2">
          <StoreFilter value={store} onChange={setStore} testid="dashboard-store-filter" />
          <Button
            data-testid="sync-base44-btn"
            variant="outline"
            onClick={syncBase44}
            disabled={syncing}
            className="border-border"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            Sync Base44
          </Button>
        </div>
      </div>

      {/* North star */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={DollarSign}
          label="Profit (all-time)"
          value={fmtCurrency(k.profit)}
          sub={`${k.margin_pct}% margin`}
          tone="success"
          testid="kpi-profit"
        />
        <KpiCard
          icon={TrendingUp}
          label="Revenue (all-time)"
          value={fmtCurrency(k.total_revenue)}
          sub={`${k.total_orders} orders processed`}
          testid="kpi-revenue"
        />
        <KpiCard
          icon={TrendingDown}
          label="COGS"
          value={fmtCurrency(k.total_cost)}
          sub="Raw + appliance cost"
          testid="kpi-cogs"
        />
        <KpiCard
          icon={Package}
          label="Open orders"
          value={k.open_orders}
          sub={`${k.today_orders} new today`}
          tone="warn"
          testid="kpi-open"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KpiCard
          icon={Paintbrush}
          label="Items in Paint"
          value={k.items_in_paint}
          tone="paint"
          testid="kpi-in-paint"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Ready to ship"
          value={k.items_ready}
          tone="success"
          testid="kpi-ready"
        />
        {stripeKpi ? (
          <StripeKpiCard data={stripeKpi} payouts={payouts} />
        ) : (
          <KpiCard
            icon={TrendingUp}
            label="Revenue today"
            value={fmtCurrency(k.today_revenue)}
            testid="kpi-today-rev"
          />
        )}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="surface-card lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-eyebrow">Recent orders</div>
              <h2 className="font-heading text-xl font-semibold mt-1">Latest pulls from Base44</h2>
            </div>
            <Link
              to="/orders"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              data-testid="view-all-orders"
            >
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-border/70">
            {data.recent_orders.map((o) => {
              const total = o.total ?? o.items.reduce((s, it) => s + (it.line_total ?? it.unit_price * it.qty), 0);
              return (
                <Link
                  key={o.id}
                  to={`/orders/${o.id}`}
                  data-testid={`recent-order-${o.number}`}
                  className="grid grid-cols-12 gap-3 py-3 items-center hover:bg-secondary/50 -mx-3 px-3 rounded-md transition-colors"
                >
                  <div className="col-span-4 sm:col-span-3 font-mono text-sm">{o.number}</div>
                  <div className="col-span-8 sm:col-span-5 text-sm truncate">
                    <div className="font-medium truncate">{o.customer.name}</div>
                    <div className="text-xs text-muted-foreground">{o.items.length} items · {fmtDateShort(o.created_at)}</div>
                  </div>
                  <div className="hidden sm:block sm:col-span-2"><StatusPill status={o.status} /></div>
                  <div className="col-span-12 sm:col-span-2 text-right num font-semibold">{fmtCurrency(total)}</div>
                </Link>
              );
            })}
            {data.recent_orders.length === 0 && (
              <div className="py-8 text-center text-muted-foreground text-sm">No orders yet. Sync from Base44.</div>
            )}
          </div>
        </div>

        <RecentActivity events={activity} />
      </div>
    </div>
  );
}
